import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Quote, Send, Plus } from "lucide-react";
import { SITE } from "@/constants/testIds";
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { name: "", message: "" };

export default function Testimonials() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);

  const fetchTestimonials = async () => {
    try {
      const { data } = await axios.get(`${API}/testimonials`);
      setItems(data);
    } catch {
      // stay silent — section falls back to empty-state
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTestimonials();
  }, []);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.message.trim()) {
      toast.error("Please share your name and a short message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/testimonials`, {
        name: form.name.trim(),
        message: form.message.trim(),
      });
      toast.success(
        "Thank you! Your story will appear here once Mike has reviewed it."
      );
      setForm(initial);
      setOpen(false);
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.message ||
        "Could not submit. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Could not submit.");
    } finally {
      setSubmitting(false);
    }
  };

  const hasItems = items.length > 0;

  return (
    <section
      id="testimonials"
      data-testid={SITE.testimonials}
      className="relative bg-gradient-to-b from-brand-cream to-brand-sand/30 py-24 md:py-32"
    >
      <div className="mx-auto max-w-6xl px-6 md:px-10">
        <div className="mx-auto max-w-2xl text-center">
          <span className="text-xs uppercase tracking-[0.35em] text-brand-evergreen/60">
            Testimonials
          </span>
          <h2 className="mt-3 font-display text-4xl leading-tight text-brand-evergreen sm:text-5xl lg:text-6xl">
            Stories from the river
          </h2>
          <p className="mt-5 text-base text-brand-charcoal/65">
            {hasItems
              ? "Kind words from students who've spent a morning on the water with us."
              : "Kind words from students are on their way."}
          </p>
        </div>

        {hasItems ? (
          <div
            data-testid={SITE.testimonialsList}
            className="mt-16 grid gap-6 md:grid-cols-2 lg:grid-cols-3"
          >
            {items.map((t) => (
              <article
                key={t.id}
                data-testid={SITE.testimonialCard(t.id)}
                className="group relative flex flex-col overflow-hidden rounded-3xl bg-white p-8 shadow-[0_20px_50px_-30px_rgba(35,66,53,0.35)] ring-1 ring-brand-evergreen/10 transition-all duration-500 hover:-translate-y-1"
              >
                <Quote className="h-8 w-8 text-brand-sand" />
                <p className="mt-5 flex-1 font-display text-xl italic leading-relaxed text-brand-charcoal/85">
                  &ldquo;{t.message}&rdquo;
                </p>
                <div className="mt-8 flex items-center gap-3 border-t border-brand-evergreen/10 pt-5">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-evergreen text-sm font-medium text-brand-cream">
                    {t.name.trim().slice(0, 1).toUpperCase()}
                  </span>
                  <div>
                    <div className="text-sm font-medium text-brand-evergreen">
                      {t.name}
                    </div>
                    <div className="text-xs text-brand-charcoal/50">Student</div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="mx-auto mt-14 max-w-2xl rounded-3xl border border-dashed border-brand-evergreen/25 bg-white/60 p-12 text-center backdrop-blur">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-brand-evergreen/10 text-brand-evergreen">
              <Quote className="h-6 w-6" />
            </div>
            <p className="mt-6 font-display text-2xl italic leading-relaxed text-brand-evergreen/80 md:text-3xl">
              {loading
                ? "Loading..."
                : "This page will soon be filled with stories from students."}
            </p>
            <p className="mt-4 text-sm text-brand-charcoal/60">
              If you&rsquo;ve taken a lesson, we&rsquo;d love to hear from you.
            </p>
          </div>
        )}

        <div className="mt-14 flex justify-center">
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <button
                type="button"
                data-testid={SITE.testimonialsSubmitOpen}
                className="inline-flex items-center gap-2 rounded-full border border-brand-evergreen/30 bg-white px-7 py-3.5 text-sm font-medium text-brand-evergreen transition-all duration-300 hover:bg-brand-evergreen hover:text-brand-cream"
              >
                <Plus className="h-4 w-4" />
                Share your experience
              </button>
            </DialogTrigger>
            <DialogContent className="max-w-lg bg-brand-cream text-brand-charcoal">
              <DialogHeader>
                <DialogTitle className="font-display text-3xl text-brand-evergreen">
                  Share your story
                </DialogTitle>
                <DialogDescription className="text-sm text-brand-charcoal/65">
                  Tell us about your time on the water. Mike will review your
                  words before they appear here — usually within a few days.
                </DialogDescription>
              </DialogHeader>
              <form
                onSubmit={submit}
                data-testid={SITE.testimonialsForm}
                className="mt-4 space-y-5"
              >
                <label className="block">
                  <span className="text-xs uppercase tracking-[0.2em] text-brand-evergreen/70">
                    Your name
                  </span>
                  <input
                    data-testid={SITE.testimonialsInputName}
                    type="text"
                    value={form.name}
                    onChange={update("name")}
                    required
                    maxLength={120}
                    className="mt-2 w-full rounded-lg border border-brand-evergreen/15 bg-white px-4 py-3 text-base text-brand-charcoal outline-none transition focus:border-brand-evergreen focus:ring-2 focus:ring-brand-evergreen/15"
                  />
                </label>
                <label className="block">
                  <span className="text-xs uppercase tracking-[0.2em] text-brand-evergreen/70">
                    Your testimonial
                  </span>
                  <textarea
                    data-testid={SITE.testimonialsInputMessage}
                    value={form.message}
                    onChange={update("message")}
                    required
                    rows={5}
                    maxLength={1500}
                    placeholder="What did you learn? How did it feel?"
                    className="mt-2 w-full resize-none rounded-lg border border-brand-evergreen/15 bg-white px-4 py-3 text-base text-brand-charcoal outline-none transition focus:border-brand-evergreen focus:ring-2 focus:ring-brand-evergreen/15"
                  />
                </label>
                <button
                  type="submit"
                  data-testid={SITE.testimonialsSubmit}
                  disabled={submitting}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand-evergreen px-7 py-3.5 text-sm font-medium tracking-wide text-brand-cream transition-all duration-300 hover:bg-brand-sand hover:text-brand-evergreen disabled:opacity-60"
                >
                  <Send className="h-4 w-4" />
                  {submitting ? "Sending..." : "Submit for review"}
                </button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </section>
  );
}
